import React, { useState, useRef, memo } from "react";
import styled from "styled-components";

// Styled Components for UI Consistency
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6); /* Semi-transparent black */
  z-index: 1000;
`;

const ModalContainer = styled.div`
  width: 312px;
  padding: 24px;
  background-color: #004d4d; /* Dark teal */
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h2`
  font-family: Verdana, sans-serif;
  font-size: 18px;
  font-weight: bold;
  color: #d9ffff; /* Light teal */
  text-align: center;
  margin-bottom: 16px;
`;

const InputField = styled.input`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border-radius: 8px;
  border: 2px solid #ffffff44; /* Subtle border */
  text-align: center;
  outline: none;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-family: "Verdana", sans-serif;
  transition: border-color 0.3s ease-in-out;
  margin-bottom: 16px;

  &::placeholder {
    color: #ffffff99;
  }

  &:focus {
    border-color: #10b981; /* Emerald Green */
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 10px;
  background-color: #10b981; /* Emerald green */
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-family: "Verdana", sans-serif;
  font-weight: bold;
  border: none;
  cursor: pointer;
  margin-right: 8px;
  transition: background-color 0.3s ease-in-out;

  &:hover {
    background-color: #059669; /* Darker Emerald */
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px;
  background-color: transparent;
  color: white;
  border: 1px solid white;
  border-radius: 6px;
  font-size: 14px;
  font-family: "Verdana", sans-serif;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  transition: background-color 0.3s ease-in-out, border-color 0.3s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: #fff;
  }
`;

/**
 * Custom Hook for Redeem Coupon Modal
 */
const useRedeemCoupon = () => {
  const [isVisible, setIsVisible] = useState(false);
  const resolveRef = useRef<((value: string | null) => void) | null>(null);

  // Open modal and reset input
  const confirm = (): Promise<string | null> => {
    return new Promise((resolve) => {
      setIsVisible(true);
      resolveRef.current = resolve;
    });
  };

  // Handle confirm action
  const handleConfirm = (couponCode: string) => {
    if (resolveRef.current) resolveRef.current(couponCode);
    setIsVisible(false);
  };

  // Handle cancel action
  const handleCancel = () => {
    if (resolveRef.current) resolveRef.current(null);
    setIsVisible(false);
  };

  return {
    confirm,
    RedeemCouponModal: memo(() =>
      isVisible ? <RedeemCouponModal handleConfirm={handleConfirm} handleCancel={handleCancel} /> : null
    ),
  };
};

/**
 * Separated Memoized Modal Component to prevent re-renders
 */
const RedeemCouponModal: React.FC<{ handleConfirm: (code: string) => void; handleCancel: () => void }> = memo(
  ({ handleConfirm, handleCancel }) => {
    const [couponCode, setCouponCode] = useState("");

    return (
      <Overlay>
        <ModalContainer>
          <ModalTitle>🎟 Redeem Your Coupon</ModalTitle>
          <InputField
            type="text"
            placeholder="Enter coupon code..."
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            autoFocus
          />
          <ActionButtons>
            <ConfirmButton onClick={() => handleConfirm(couponCode)}>Redeem</ConfirmButton>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          </ActionButtons>
        </ModalContainer>
      </Overlay>
    );
  }
);

export default useRedeemCoupon;
